import React, { useState, useEffect } from 'react';
import SEOMetaTags from '../components/SEOMetaTags';
import Header from '../components/Header';
import { trackPageView, trackButtonClick } from '../analytics';

const TransformPdf = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [linearize, setLinearize] = useState(true);
  const [fix, setFix] = useState(false);

  // Maximum file size in bytes (10MB)
  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  // Track page view
  useEffect(() => {
    if (typeof window !== 'undefined') {
      trackPageView(window.location.pathname);
    }
  }, []);

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setError(null);
    setSuccess(false);

    if (file && !file.type.includes('pdf')) {
      setError('Please select a PDF file.');
      setSelectedFile(null);
      return;
    }

    if (file && file.size > MAX_FILE_SIZE) {
      setError('File size exceeds 10MB limit.');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  // Handle PDF transformation
  const handleTransform = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file.');
      return;
    }

    if (!linearize && !fix) {
      setError('Please select at least one transformation option (Linearize or Repair).');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      trackButtonClick('transform_pdf', 'transform_pdf');

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('linearize', linearize);
      formData.append('fix', fix);

      const response = await fetch('https://api.temp2doc.com/organise/transform', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      // Get the transformed PDF
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      // Create a download link and trigger it
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `transformed_${selectedFile.name}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(true);
      trackButtonClick('transform_success', 'transform_pdf');
    } catch (err) {
      trackButtonClick('transform_error', 'transform_pdf');
      setError(`Failed to transform PDF: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEOMetaTags 
          title="Repair & Transform PDF - Linearize & Repair PDF Files | Temp2Doc"
          description="Optimize PDFs for fast web viewing with linearization and repair broken PDF structures. Free online PDF transformation tool with no installation required. Fix corrupted PDFs and enable progressive loading for web browsers."
          keywords="transform PDF, linearize PDF, repair PDF, optimize PDF, fix PDF, web-optimized PDF, fast PDF loading, PDF structure repair, fix corrupted PDF, PDF fast web view, progressive PDF loading, normalize PDF, repair broken PDF, PDF optimization tool"
          canonicalUrl="https://temp2doc.com/transform-pdf"
          structuredData={{
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Repair & Transform PDF",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "description": "Optimize PDFs for fast web viewing with linearization and repair broken PDF structures. Free online PDF transformation tool with no installation required.",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "1250"
            },
            "featureList": [
              "Linearize PDF for fast web viewing",
              "Repair corrupted or broken PDF files",
              "Normalize PDF content and structure",
              "Progressive page loading optimization",
              "No file size limits up to 10MB",
              "Secure processing with automatic file deletion",
              "No registration or login required",
              "Free online tool"
            ]
          }}
        />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-white to-blue-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <section className="max-w-6xl mx-auto text-center mb-12">
          <div className="inline-block p-3 bg-primary-100 rounded-full mb-4">
            <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Repair & Transform PDF
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Optimize your PDFs for fast web viewing with linearization or repair broken PDF structures.
          </p>
        </section>

        {/* Main Tool Section */}
        <section className="max-w-6xl mx-auto mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            {/* File Upload */}
            <div className="mb-6">
              <label htmlFor="pdf-upload" className="block text-sm font-medium text-gray-700 mb-2">
                Select PDF File
              </label>
              <div className="relative">
                <input
                  id="pdf-upload"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={isLoading}
                />
                <label
                  htmlFor="pdf-upload"
                  className={`flex items-center justify-center w-full px-4 py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    isLoading ? 'border-gray-300 bg-gray-50 cursor-not-allowed' : 'border-primary-300 hover:border-primary-500 bg-primary-50 hover:bg-primary-100'
                  }`}
                >
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-primary-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <p className="mt-2 text-sm text-gray-600">
                      {selectedFile ? selectedFile.name : 'Click to upload PDF file'}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">Maximum file size: 10MB</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Transformation Options */}
            <div className="mb-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Transformation Options</h3>
              
              {/* Linearize Option */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="linearize"
                    type="checkbox"
                    checked={linearize}
                    onChange={(e) => setLinearize(e.target.checked)}
                    disabled={isLoading}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                </div>
                <div className="ml-3">
                  <label htmlFor="linearize" className="font-medium text-gray-700">
                    Linearize PDF (Fast Web View)
                  </label>
                  <p className="text-sm text-gray-500">
                    Optimize PDF for streaming and faster loading in web browsers. Pages load progressively instead of downloading the entire file first.
                  </p>
                </div>
              </div>

              {/* Repair Option */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="fix"
                    type="checkbox"
                    checked={fix}
                    onChange={(e) => setFix(e.target.checked)}
                    disabled={isLoading}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                </div>
                <div className="ml-3">
                  <label htmlFor="fix" className="font-medium text-gray-700">
                    Repair PDF Structure
                  </label>
                  <p className="text-sm text-gray-500">
                    Fix corrupted or broken PDF files by normalizing content and repairing structural issues. Helps recover damaged PDFs.
                  </p>
                </div>
              </div>
            </div>

            {/* Transform Button */}
            <button
              onClick={handleTransform}
              disabled={!selectedFile || isLoading || (!linearize && !fix)}
              className={`w-full flex items-center justify-center px-6 py-3 rounded-lg text-white font-medium transition-colors ${
                !selectedFile || isLoading || (!linearize && !fix)
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700'
              }`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Transforming PDF...
                </>
              ) : (
                'Transform PDF'
              )}
            </button>

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg" role="alert">
                {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg" role="alert">
                PDF transformed successfully! Your download should start automatically.
              </div>
            )}
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-primary-600 font-bold text-lg">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Upload PDF</h3>
              <p className="text-gray-600">Select the PDF file you want to transform. Maximum file size is 10MB.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-primary-600 font-bold text-lg">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Choose Options</h3>
              <p className="text-gray-600">Select linearization for web optimization, repair for fixing broken PDFs, or both.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-primary-600 font-bold text-lg">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Download</h3>
              <p className="text-gray-600">Your transformed PDF will be processed and downloaded automatically.</p>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Key Features</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">PDF Linearization</h3>
              <p className="text-gray-600 text-sm">
                Optimize PDFs for fast web viewing with progressive page loading.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">PDF Repair</h3>
              <p className="text-gray-600 text-sm">
                Fix corrupted PDF files by repairing structural issues and errors.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure & Private</h3>
              <p className="text-gray-600 text-sm">
                Files are processed securely and deleted immediately after transformation.
              </p>
            </div>
          </div>
        </section>

        {/* Related Tools Section */}
        <section className="max-w-6xl mx-auto">
          <nav className="bg-white rounded-lg shadow-md p-6 border border-gray-200" aria-label="Related Tools">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related PDF Tools</h2>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <li>
              <a href="/chat-with-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Chat PDF With AI</span>
              </a>
            </li>
            <li>
              <a href="/merge-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Merge PDF</span>
              </a>
            </li>
            <li>
              <a href="/split-remove-rotate-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Split Remove Rotate PDF</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-word" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Word</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-excel" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Excel</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-image" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to Image</span>
              </a>
            </li>
            <li>
              <a href="/pdf-to-powerpoint" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">PDF to PowerPoint</span>
              </a>
            </li>
            <li>
              <a href="/protect-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Protect PDF</span>
              </a>
            </li>
            <li>
              <a href="/unlock-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Unlock PDF</span>
              </a>
            </li>
            <li>
              <a href="/compress-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Compress PDF</span>
              </a>
            </li>
            <li>
              <a href="/diagnose-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Verify & Diagnose PDF</span>
              </a>
            </li>
            <li>
              <a href="/" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                <span className="text-primary-600 font-medium">Back to Home</span>
              </a>
            </li>
          </ul>
          </nav>
        </section>
      </main>
    </>
  );
};

export default TransformPdf;
